import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { I18N_KEYS } from '@/language/keys';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useWorkflowStore } from '@/state/workflow.state';
import { approveModelLegalTerms } from '@/api/workspaces.api';
import { log } from '@/logger/logger';
import { getUnverifiedModelRunDialogInfoFromNodeTypes } from './unverifiedModelRunDialog.utils';
import type { NodeDefinition } from '@/types/api/nodeDefinition';

const logger = log.getLogger('useUnverifiedModelRunDialog');

const useUnverifiedModelRunDialog = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { openConfirmationDialog } = useConfirmationDialogStore();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);

  const openConfirmUnverifiedModelRun = useCallback(
    async ({
      eventName,
      nodeTypes,
      model,
      onConfirm,
      trackType = TrackTypeEnum.BI,
    }: {
      eventName: string;
      nodeTypes: NodeDefinition[];
      model: { id: string };
      onConfirm: () => Promise<void>;
      trackType?: TrackTypeEnum;
    }) => {
      const { modelsNameDisplay, termsLinks } = getUnverifiedModelRunDialogInfoFromNodeTypes({
        nodeTypes,
        model,
      });

      track(eventName, { modelId: model.id, modelsNameDisplay, ...termsLinks }, trackType);
      let i18nContext = '';
      let title = t(I18N_KEYS.RECIPE_MAIN.FLOW.UNVERIFIED_MODEL_RUN.DIALOG.TITLE);
      if (!termsLinks.termsUrl) {
        // TODO: for imported models
        i18nContext = 'noterms';
        title = t(I18N_KEYS.RECIPE_MAIN.FLOW.UNVERIFIED_MODEL_RUN.DIALOG_IMPORTED_MODEL.TITLE);
      } else {
        i18nContext = termsLinks.additionalTermsLink ? 'additionalTermsLink' : '';
      }

      return await new Promise<void>((resolve, reject) => {
        openConfirmationDialog({
          title,
          messageData: {
            i18n: {
              i18nKey: I18N_KEYS.RECIPE_MAIN.FLOW.UNVERIFIED_MODEL_RUN.DIALOG.TEXT,
              context: i18nContext,
              values: { modelsList: modelsNameDisplay },
            },
            data: termsLinks,
          },
          confirmBtnText: t(I18N_KEYS.GENERAL.ACCEPT),
          cancelBtnText: t(I18N_KEYS.GENERAL.CANCEL),
          disableBackdropClose: true,
          width: 'md',
          onConfirm: async () => {
            track(
              'unverified_model_run_dialog_confirm',
              { modelId: model.id, modelsNameDisplay, ...termsLinks },
              trackType,
            );
            await onConfirm();
            resolve();
          },
          onClose: () => reject(new Error('')),
        });
      });
    },
    [openConfirmationDialog, t, track],
  );

  /**
   * Handles error code UNVERIFIED_MODEL_NOT_APPROVED (unverified model check) by showing a confirmation dialog
   * and allowing the user to approve the model terms (1 dialog per model).
   */
  const handleUnverifiedModelError = useCallback(
    async (error: AxiosError): Promise<null> => {
      const errorData = error.response?.data;
      const errorMessage =
        errorData &&
        typeof errorData === 'object' &&
        'message' in errorData &&
        typeof (errorData as { message?: unknown }).message === 'string'
          ? (errorData as { message: string }).message
          : error.message;

      const modelsData =
        typeof errorMessage === 'string'
          ? errorMessage
              .split(',')
              .map((m) => ({ id: m.trim() }))
              .filter((m) => Boolean(m.id))
          : [];

      try {
        // Show 1 dialog per model (stack), require explicit approval for each before continuing.
        for (const model of modelsData) {
          await openConfirmUnverifiedModelRun({
            eventName: 'unverified_model_run_dialog_open',
            nodeTypes,
            model,
            onConfirm: async () => await approveModelLegalTerms([model.id]),
          });
        }
        return null;
      } catch (e) {
        logger.error('Error approving model legal terms', e);
        return null;
      }
    },
    [openConfirmUnverifiedModelRun, nodeTypes],
  );

  return { handleUnverifiedModelError };
};
export default useUnverifiedModelRunDialog;
